import type { Signer } from '@ethersproject/abstract-signer'
import { getAddress } from '@ethersproject/address'
import { BigNumber } from '@ethersproject/bignumber'
import { AddressZero } from '@ethersproject/constants'
import { Contract } from '@ethersproject/contracts'
import type { Provider } from '@ethersproject/providers'
import { ChainId, Currency } from '@pancakeswap/sdk'
import { bsc } from 'wagmi/chains'
import memoize from 'lodash/memoize'
import { TokenAddressMap } from '@pancakeswap/token-lists'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { chains, xLayer } from './wagmi'

const zeroAddress = '0x0000000000000000000000000000000000000000'

// returns the checksummed address if the address is valid, otherwise returns false
export const isAddress = memoize((value: any): string | false => {
  try {
    return getAddress(value)
  } catch {
    return false
  }
})

export function getBlockExploreLink(
  data: string | number,
  type: 'transaction' | 'token' | 'address' | 'block' | 'countdown',
  chainIdOverride?: number,
): string {
  const chainId = chainIdOverride || ChainId.X_LAYER
  const chain = chains.find((c) => c.id === chainId)
  if (!chain) return xLayer.blockExplorers.default.url
  switch (type) {
    case 'transaction': {
      return `${chain.blockExplorers.default.url}/tx/${data}?channelId=potato`
    }
    case 'token': {
      return `${chain.blockExplorers.default.url}/token/${data}?channelId=potato`
    }
    case 'block': {
      return `${chain.blockExplorers.default.url}/block/${data}?channelId=potato`
    }
    case 'countdown': {
      return `${chain.blockExplorers.default.url}/block/countdown/${data}?channelId=potato`
    }
    default: {
      return `${chain.blockExplorers.default.url}/address/${data}?channelId=potato`
    }
  }
}

export function getBlockExploreName(chainIdOverride?: number) {
  const chainId = chainIdOverride || ChainId.BSC
  const chain = chains.find((c) => c.id === chainId)

  return chain?.blockExplorers?.default.name || bsc.blockExplorers.default.name
}

export function getBscScanLinkForNft(collectionAddress: string, tokenId: string): string {
  return `${bsc.blockExplorers.default.url}/token/${collectionAddress}?a=${tokenId}`
}

// add 10%
export function calculateGasMargin(value: BigNumber, margin = 1000): BigNumber {
  return value.mul(BigNumber.from(10000).add(BigNumber.from(margin))).div(BigNumber.from(10000))
}

// account is optional
export function getContract(address: string, ABI: any, signer?: Signer | Provider): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }

  return new Contract(address, ABI, signer)
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function isTokenOnList(defaultTokens: TokenAddressMap<ChainId>, currency?: Currency): boolean {
  if (currency?.isNative) return true
  return Boolean(currency?.isToken && defaultTokens[currency.chainId]?.[currency.address])
}

export function cn(...inputs: any[]) {
  return twMerge(clsx(inputs))
}


export const formatNumber = (value: number | string, decimals: number = 2): string => {
  if (value === 0 || Number.isNaN(Number(value))) {
    return '0'
  }
  const num = Number(value)
  if (Math.abs(num) < 0.00001) return '< 0.00001'
  const fixedValue = num.toFixed(decimals)
  return fixedValue.replace(/\.?0+$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

// 导出 viem 客户端工具函数
export { publicClient, createViemWalletClient, createViemPublicClient } from './viemClients';

export const estimateAndCheckGas = async (okbBalance: any): Promise<boolean> => {
  try {
    // const defaultGas = BigInt(21000); // 基本交易的默认 gas
    
    // 检查用户是否有足够的 gas
    if (okbBalance && okbBalance.value <= 0) {
      return false;
    }
    return true;
  } catch (error) {
    console.error('Gas estimation failed:', error);
    return true;
  }
};

// 只添加3位分隔符，增强鲁棒性
export const formatNumberWithCommas = (value: string | number | undefined | null): string => {
  if (value === undefined || value === null) return '0';
  
  // 尝试将输入转换为数字，处理科学计数法
  let numStr: string;
  try {
    // 先移除已有的逗号
    const cleanValue = value.toString().replace(/,/g, '');
    // 转换为数字再转回字符串，处理科学计数法
    numStr = Number(cleanValue).toString();
    // 如果转换结果是NaN，返回0
    if (numStr === 'NaN') return '0';
  } catch (error) {
    console.error('formatNumberWithCommas error:', error);
    return '0';
  }
  
  // 处理小数点
  const parts = numStr.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  return parts.join('.');
};

// 将数字转换为带有K、M、B等单位的格式
export const formatNumberToUnit = (value: number | string, decimals: number = 2): string => {
  if (value === undefined || value === null || value === '' || Number.isNaN(Number(value))) {
    return '0';
  }
  
  const num = Number(value);
  
  if (num === 0) return '0';
  if (Math.abs(num) < 0.00001) return '< 0.00001';
  
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  
  if (absNum >= 1000000000) {
    // 十亿及以上用B (Billion)
    return `${sign}${(absNum / 1000000000).toFixed(decimals).replace(/\.?0+$/, '')}B`;
  }
  if (absNum >= 1000000) {
    // 百万及以上用M (Million)
    return `${sign}${(absNum / 1000000).toFixed(decimals).replace(/\.?0+$/, '')}M`;
  }
  if (absNum >= 1000) {
    // 千及以上用K (Kilo)
    return `${sign}${(absNum / 1000).toFixed(decimals).replace(/\.?0+$/, '')}K`;
  }
  // 小于1000的数字直接显示
  return `${sign}${absNum.toFixed(decimals).replace(/\.?0+$/, '')}`;
};


export const isValidAddress = (address: string | undefined): boolean => {
  if (!address) return false;
  return Boolean(isAddress(address) && address !== zeroAddress);
};

// 格式化价格，保留6位小数，数值太小时使用0.{n}格式显示
export const formatPrice = (price: string | number): string => {
  if (price === undefined || price === null || price === '' || Number.isNaN(Number(price))) {
    return '0.00';
  }
  
  const num = Number(price);
  if (num === 0) return '0.00';
  
  // 对于小于0.000001的价格，使用0.{n}格式
  if (Math.abs(num) < 0.000001) {
    // 极小的数值使用科学记数法
    if (Math.abs(num) < 1e-10) {
      return num.toExponential(2);
    }
    
    // 计算前导零的个数
    const numStr = num.toString();
    if (numStr.includes('e')) {
      // 处理科学记数法形式的输入
      const [coefficientStr, exponent] = numStr.split('e');
      const exp = Math.abs(parseInt(exponent));
      const coefficient = parseFloat(coefficientStr);
      const significantStr = coefficient.toString().replace('.', '');
      const significantDigits = significantStr.substring(0, 2).padEnd(2, '0');
      return `0.{${exp - 1}}${significantDigits}`;
    }
    // 处理普通小数形式
    const decimalPart = numStr.split('.')[1] || '';
    let zeroCount = 0;
    for (let i = 0; i < decimalPart.length; i++) {
      if (decimalPart[i] === '0') {
        zeroCount++;
      } else {
        break;
      }
    }
    const remainingDigits = decimalPart.substring(zeroCount);
    const significantDigits = remainingDigits.substring(0, 2).padEnd(2, '0');
    return `0.{${zeroCount}}${significantDigits}`;
  }
  
  // 其他情况保留6位小数，但至少保留2位
  const formatted = num.toFixed(6);
  // 移除末尾的0，但至少保留2位小数
  const parts = formatted.split('.');
  if (parts[1]) {
    const trimmed = parts[1].replace(/0+$/, '');
    const minDecimals = Math.max(2, trimmed.length);
    return num.toFixed(minDecimals);
  }
  return num.toFixed(2);
};

// 格式化价格变化率百分比
export const formatPriceChangeRate = (rate: string | number): string => {
  if (rate === undefined || rate === null || rate === '' || Number.isNaN(Number(rate))) {
    return '0.00%';
  }
  
  const num = Number(rate);
  // rate已经是小数形式，直接乘以100转为百分比
  const percentage = num * 100;
  return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
};